import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { permsOf } from "@/lib/perms";

const AuthContext = createContext(null);

const isDev = process.env.NODE_ENV === "development";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      if (isDev && e?.response?.status !== 401) {
        console.error("Auth check failed:", e);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (isDev) console.error("Logout failed:", e);
    }
    setUser(null);
  }, []);

  const can = useMemo(() => {
    const perms = permsOf(user);
    return (action) => perms.includes(action);
  }, [user]);

  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout, can }),
    [user, loading, checkAuth, logout, can]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};
